import { combineReducers } from "redux"
import { connectRouter } from "connected-react-router"
import { History } from "history"
import { reducer as toastrReducer } from "react-redux-toastr"
import { persistReducer } from "redux-persist"
import storage from "redux-persist/lib/storage"

import { runQCReducer } from "../data/stores/run-qc/run-qc-api-reducer"
import { sampleReducer } from "../data/stores/sample-setup/sample-setup-api-reducer"
import { coreReducer } from "./store/core-reducers"
import { authReducer } from "../core-components/login/store/login-reducers"
import { runDesignReducer } from "../data/stores/run-design/run-design-api-reducer"
import { usersReducer } from "../data/stores/users/users-api-reducer"
import { datasetSelectorReducer } from "../data/stores/dataset-selector/dataset-selector-api-reducer"
import { analysisResultsReducer } from "../data/stores/analysis/analysis-results/analysis-results-reducer"
import { partsReducer } from "../data/stores/parts/parts-api-reducer"
import { filenamePrefixReducer } from "../data/stores/filename-prefix/filename-prefix-reducer"
import { analysisPollingReducer } from "../data/stores/analysis/analysis-polling/analysis-polling-reducer"
import { rulesReducer } from "../data/stores/rules/rules-reducer"
import { multiJobPollingReducer } from "../data/stores/analysis/multijob-polling/multijob-polling-reducer"
import { datasetDetailsReducer } from "../data/stores/dataset-details/dataset-details-reducer"
import { runSelectorReducer } from "../data/stores/run-selector/run-selector-reducer"
import { projectReducer } from "../data/stores/project/project-api-reducer"
import { alarmReducer } from "../data/stores/alarm/alarm-api-reducer"
import { aboutReducer } from "../data/stores/about/about-api-reducer"
import { analysisSelectorReducer } from "../data/stores/analysis/analysis-selector/analysis-selector-reducer"
import { newAnalysisReducer } from "../data/stores/analysis/new-analysis/new-analysis-reducer"
import { devReducer } from "../data/stores/dev/dev-reducer"
import { barcodeSampleNamesReducer } from "../data/stores/barcode-sample-names/barcode-sample-names-reducer"
import { notificationsReducer } from "../data/stores/notifications/notifications-reducer"
import { instrumentTableReducer } from "../data/stores/instrument-table.ts/instrument-table-reducer"
import { instrumentSettingsReducer } from "../data/stores/instrument-settings/instrument-settings-reducer"
import { entryPointsReducer } from "../data/stores/entry-points.ts/entry-points-reducer"
import { userPreferencesReducer } from "../data/stores/user-preferences/user-preferences-reducer"
import { templateFilesReducer } from "../data/stores/template-files/template-files-reducer"
import { tenantReducer } from "src/data/stores/tenant/tenant-reducer"
import { analysisOptionsReducer } from "src/data/stores/analysis-options/analysis-options-reducer"
import { instrumentLoginReducer } from "../data/stores/instrument-login/instrument-login-api-reducer"
import { inAppCommsReducer } from "../data/stores/in-app-comms/in-app-comms-reducer"
import { whatsNewReducer } from "../data/stores/whats-new/whats-new-reducer"

export const createRootReducer = (history: History) => {
    const rootPersistConfig = {
        key: "root",
        storage,
        whitelist: ["auth"]
    }

    const authPersistConfig = {
        key: "auth",
        storage,
        blacklist: ["loading"]
    }

    const prefixPersistConfig = {
        key: "prefix",
        storage,
        blacklist: ["loading"]
    }

    const projectPersistConfig = {
        key: "project",
        storage,
        blacklist: ["loading"]
    }

    const aboutPersistConfig = {
        key: "about",
        storage,
        blacklist: [
            "chemistryBundleCurrentVersionLoading:", "chemistryBundleCurrentVersionError",
            "chemistryBundleUpdateAvailableLoading", "chemistryBundleUpdateAvailableError",
            "chemistryBundleUpgradeLoading", "chemistryBundleUpgradeError",
            "uiBundleBundleCurrentVersionLoading", "uiBundleBundleCurrentVersionError",
            "uiBundleBundleUpdateAvailableLoading", "uiBundleBundleUpdateAvailableError",
            "uiBundleBundleUpgradeLoading", "uiBundleBundleUpgradeError"
        ]
    }

    const devPersistConfig = {
        key: "dev",
        storage,
        blacklist: ["loading"]
    }

    // Persist only the event retry queue so that interaction events
    // (dismiss, ack) queued during a network failure survive a page reload.
    const inAppCommsPersistConfig = {
        key: "inAppComms",
        storage,
        whitelist: ["pendingEvents"],
    }

    const rootReducer = combineReducers({
        alarm: alarmReducer,
        core: coreReducer,
        auth: persistReducer(authPersistConfig, authReducer),
        userPreferences: userPreferencesReducer,
        runQC: runQCReducer,
        instrumentTable: instrumentTableReducer,
        sampleSetup: sampleReducer,
        runDesign: runDesignReducer,
        analysisOptions: analysisOptionsReducer,
        users: usersReducer,
        datasetSelector: datasetSelectorReducer,
        project: persistReducer(projectPersistConfig,projectReducer),
        datasetDetails: datasetDetailsReducer,
        analysisResults: analysisResultsReducer,
        analysisPoller: analysisPollingReducer,
        multiJobsPoller: multiJobPollingReducer,
        analysisSelector: analysisSelectorReducer,
        newAnalysis: newAnalysisReducer,
        runSelector: runSelectorReducer,
        rules: rulesReducer,
        toastr: toastrReducer,
        router: connectRouter(history),
        parts: partsReducer,
        entryPoints: entryPointsReducer,
        prefix: persistReducer(prefixPersistConfig,filenamePrefixReducer),
        barcodeSampleNames: barcodeSampleNamesReducer,
        about: persistReducer(aboutPersistConfig, aboutReducer),
        notifications: notificationsReducer,
        instrumentSettings: instrumentSettingsReducer,
        dev: persistReducer(devPersistConfig, devReducer),
        templateFiles: (templateFilesReducer),
        tenant: tenantReducer,
        instrumentLogin: instrumentLoginReducer,
        inAppComms: persistReducer(inAppCommsPersistConfig, inAppCommsReducer),
        whatsNew: whatsNewReducer,
    })

    return persistReducer(rootPersistConfig, rootReducer)
}
